import axios, { AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { auth } from '../../config/src';
import { API_CONFIG } from '../../config/src';
import { UserSettings, ChatPayload, ChatResponse, TimelineEntry, SavedMemory, SystemPromptData } from '../../types/src';


// ======================================================================================
// ||| MENTRA API SERVICE - CLINICAL APPLICATION                                      |||
// ======================================================================================
// ||| This is the Mentra API service for the clinical application.                    |||
// ||| Uses local backend in development and production backend in live deployments.  |||
// ======================================================================================

const apiClient = axios.create({
    baseURL: API_CONFIG.baseUrl,
    timeout: API_CONFIG.timeout,
    headers: { 'Content-Type': 'application/json' },
});

// DEBUG: Log the actual base URL being used
console.log('🔧 [MENTRA API SERVICE] Base URL:', API_CONFIG.baseUrl);
console.log('🔧 [MENTRA API SERVICE] Environment:', process.env.NODE_ENV || 'development');

apiClient.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    // Define public endpoints that don't require authentication
    const publicEndpoints = ['/api/token-packages'];
    const isPublicEndpoint = publicEndpoints.some(endpoint => config.url?.includes(endpoint));
    
    if (!isPublicEndpoint) {
        const user = auth.currentUser;
        if (user) {
            try {
                const token = await user.getIdToken(true); // Force refresh
                config.headers['Authorization'] = `Bearer ${token}`;
            } catch (error) {
                // Show user-friendly session timeout message
                console.warn('Session expired. Please refresh the page to continue.');
                
                // Dispatch a custom event to notify the app about session timeout
                const sessionTimeoutEvent = new CustomEvent('bibbit-session-timeout', {
                    detail: { 
                        message: 'Your session has expired. Please refresh the page to continue.',
                        timestamp: Date.now()
                    }
                });
                window.dispatchEvent(sessionTimeoutEvent);
                
                return Promise.reject(error);
            }
        } else {// No user found - this shouldn't happen in normal flow, but handle gracefully
            console.warn('No authenticated user found. Please log in.');
            return Promise.reject(new Error('No authenticated user found'));
        }
        
        // Read the active role from sessionStorage - default to standard_user if not set
        const activeUserRole = sessionStorage.getItem('activeUserRole') || 'standard_user';
        config.headers['X-Active-Role'] = activeUserRole;

        // Check if a child account is active and add the X-Child-Id header
        const activeChildId = sessionStorage.getItem('activeChildId');
        if (activeChildId) {
            config.headers['X-Child-Id'] = activeChildId;
        }

        // Mobile headers will be added by the mobile service directly
        // when available in the bibbit app

        // Debug logging for development
        const isDev = typeof window !== 'undefined' ? window.location.hostname === 'localhost' : false;
        if (isDev) {
            console.log('🔧 [API SERVICE] Request headers:', {
                'X-Active-Role': activeUserRole,
                'X-Child-Id': activeChildId,
                url: config.url,
                method: config.method
            });
            console.log('🔧 [API SERVICE] Full request config:', {
                baseURL: config.baseURL,
                url: config.url,
                fullURL: `${config.baseURL}${config.url}`,
                method: config.method,
                headers: config.headers
            });
        }
    }

    return config;
});

// Add response interceptor to handle 403 errors
apiClient.interceptors.response.use(
    (response: AxiosResponse) => {
        return response;
    },
    async (error) => {
        const originalRequest = error.config;
        
        // If we get a 401 or 403 error and haven't already tried to refresh the token
        if ((error.response?.status === 401 || error.response?.status === 403) && !originalRequest._retry) {
            originalRequest._retry = true;
            
            const user = auth.currentUser;
            if (user) {
                try {
                    const newToken = await user.getIdToken(true);
                    originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                    
                    // Retry the original request with the new token
                    return apiClient(originalRequest);
                } catch (refreshError) {
                    // Show user-friendly session timeout message instead of immediate redirect
                    console.warn('Session expired. Please refresh the page to continue.');
                    
                    // Dispatch a custom event to notify the app about session timeout
                    const sessionTimeoutEvent = new CustomEvent('bibbit-session-timeout', {
                        detail: { 
                            message: 'Your session has expired. Please refresh the page to continue.',
                            timestamp: Date.now()
                        }
                    });
                    window.dispatchEvent(sessionTimeoutEvent);
                    
                    return Promise.reject(refreshError);
                }
            }
        }
        
        return Promise.reject(error);
    }
);

// ======================================================================================
// ||| BIBBIT-ONLY API SERVICE CLASS                                                  |||
// ======================================================================================

class BibbitApiService {
    private readonly endpoints = {
        CHAT: '/api/chat',
        CHAT_HISTORY: '/api/chat-history',
        MEMORY: '/api/memory',
        SYSTEM_PROMPT: '/api/system-prompt',
        TOKENS: '/api/tokens',
        TOKEN_PACKAGES: '/api/token-packages',
        USER: '/api/users',
        SAVED_MEMORY: '/api/memory/users',
        TIMELINE: '/api/memory/users',
        SETTINGS: '/api/users',
        SYMBOLIC: '/api/symbolic',
    };

    private getCurrentUserId(): string {
        const user = auth.currentUser;
        if (!user) {
            throw new Error('No authenticated user');
        }
        return user.uid;
    }

    // Get the correct user ID for API calls based on active profile (parent or child)
    private getActiveUserId(): string | null {
        const user = auth.currentUser;
        if (!user) {
            return null;
        }

        // Check if a child account is active
        const activeChildId = sessionStorage.getItem('activeChildId');
        if (activeChildId) {
            return activeChildId;
        } else {return user.uid; // Return parent ID if no child is active
        }
    }

    /*
    private getCurrentConversationId(): string {
        const auth = auth;
        const user = auth.currentUser;
        if (!user) throw new Error('No authenticated user');
        
        // Check if a child account is active
        const activeChildId = sessionStorage.getItem('activeChildId');
        if (activeChildId) {
            return `${user.uid}_child_${activeChildId}`;
        }
        
        // Check if parent profile is active
        const activeUserRole = sessionStorage.getItem('activeUserRole');
        if (activeUserRole === 'family_member') {
            return `${user.uid}_parent`;
        }
        
        // Default to solo user
        return `${user.uid}_solo`;
    }
    */

    // Generic request method for flexibility
    async makeRequest<T>(endpoint: string, options?: AxiosRequestConfig): Promise<T> {
        try {
            const response = await apiClient.request<T>({ url: endpoint, ...options });
            return response.data;
        } catch (error: any) {
            // CRITICAL: Never return empty data in production mental health app
            // This could lead to dangerous misdiagnosis or treatment errors
            if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK' || error.response?.status === 503) {
                throw new Error('Backend unavailable. Please ensure backend is running.');
            }
            
            // For other errors, still throw to maintain existing behavior
            throw error;
        }
    }

    // --- CHAT ---
    async sendChatMessage(payload: ChatPayload): Promise<ChatResponse> {
        try {
            const user = this.getCurrentUserId();
            if (!user) throw new Error('User not authenticated');
            
            // DEBUG: Log the payload being sent
            console.log('🔧 [API SERVICE] sendChatMessage payload:', payload);
            
            const requestData = {
                message: payload.message,
                model: payload.model,
                contentMode: payload.contentMode,  // Send as camelCase - backend will handle conversion
                mindstreamMode: payload.mindstreamMode,  // Send as camelCase - backend will handle conversion
                localHistory: payload.localHistory,  // Send as camelCase - backend will handle conversion
                userId: payload.userId,  // Send as camelCase - backend will handle conversion
                conversationId: payload.conversationId,  // Send as camelCase - backend will handle conversion
                // Game mode fields - send as camelCase - backend will handle conversion
                ...(payload.gameMode && payload.gameType ? {
                    gameMode: payload.gameMode,
                    gameType: payload.gameType,
                    gameInstructions: payload.gameInstructions,
                    gameData: payload.gameData
                } : {})
            };
            
            // DEBUG: Log the final request data
            console.log('🔧 [API SERVICE] Final request data:', requestData);
            
            const { data } = await apiClient.post<ChatResponse>(this.endpoints.CHAT, requestData);
            return data;
        } catch (error: any) {
            // CRITICAL: Never return mock responses in production mental health app
            // This could lead to dangerous misdiagnosis or treatment errors
            if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK' || error.response?.status === 503) {
                throw new Error('Chat service unavailable. Please ensure backend is running.');
            }
            throw error;
        }
    }

    async getChatHistory(userId?: string, conversationId?: string): Promise<{ history: any[] }> {
        const activeUserId = userId;
        const params = new URLSearchParams();
        if (conversationId) {
            params.append('conversation_id', conversationId);
        }
        const queryString = params.toString();
        const url = `${this.endpoints.CHAT_HISTORY}/${activeUserId}${queryString ? `?${queryString}` : ''}`;
        
        const { data } = await apiClient.get(url);
        return data;
    }

    async clearConversationHistory(userId: string, conversationId: string): Promise<{ success: boolean; deletedCount: number; conversationId: string }> {
        try {
            const response = await apiClient.delete(`/api/chat/${userId}/conversation/${conversationId}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    // --- MEMORY & TIMELINE ---
    async saveMemory(userId: string, content: string, tags: string[]) {
        const { data } = await apiClient.post(this.endpoints.MEMORY, {
            userId: userId,  // Send as camelCase - backend will handle conversion
            content,
            tags,
        });
        return data;
    }

    async getMemories(userId: string) {
        const { data } = await apiClient.get(`${this.endpoints.MEMORY}/${userId}`);
        return data;
    }

    async getPinnedTimeline(userId: string): Promise<TimelineEntry[]> {
        const { data } = await apiClient.get<TimelineEntry[]>(`${this.endpoints.TIMELINE}/${userId}/timeline`);
        return data;
    }

    async getSavedMemories(userId: string): Promise<SavedMemory[]> {
        const { data } = await apiClient.get<SavedMemory[]>(`${this.endpoints.SAVED_MEMORY}/${userId}/saved-memories`);
        return data;
    }

    // --- USER & SETTINGS ---
    async getUserSettings(userId?: string): Promise<UserSettings> {
        const activeUserId = userId || this.getActiveUserId();
        if (!activeUserId || activeUserId === 'undefined') {
            throw new Error('Valid user ID is required');
        }
        const { data } = await apiClient.get<UserSettings>(`${this.endpoints.USER}/${activeUserId}/settings`);
        return data;
    }

    async updateUserSettings(userId: string, settings: Partial<UserSettings>): Promise<UserSettings> {
        return this.makeRequest<UserSettings>(`${this.endpoints.SETTINGS}/${userId}/settings`, {
            method: 'PATCH',
            data: settings
        });
    }

    async deleteUserAccount(userId: string): Promise<{ message: string }> {
        const { data } = await apiClient.delete(`${this.endpoints.USER}/${userId}`);
        return data;
    }

    async updateUserRole(userId: string, newRole: string): Promise<{ message: string }> {
        const { data } = await apiClient.patch(`${this.endpoints.USER}/${userId}`, { role: newRole });
        return data;
    }

    async getUserData(userId: string): Promise<any> {
        const { data } = await apiClient.get(`${this.endpoints.USER}/${userId}`);
        return data;
    }

    async getSystemStatus(): Promise<any> {
        const { data } = await apiClient.get('/system/status');
        return data;
    }

    // --- JOURNAL PROMPTS ---
    async getJournalPrompts(contentMode: string = 'FULL', params?: { category?: string; mood?: string }): Promise<any> {
        const queryParams = new URLSearchParams({ content_mode: contentMode });
        if (params?.category) queryParams.append('category', params.category);
        if (params?.mood) queryParams.append('mood', params.mood);
        
        const { data } = await apiClient.get(`/api/journal-prompts/prompts?${queryParams.toString()}`);
        return data;
    }

    async getJournalPromptCategories(contentMode: string = 'FULL'): Promise<any> {
        const { data } = await apiClient.get(`/api/journal-prompts/prompts/categories?content_mode=${contentMode}`);
        return data;
    }

    async getRandomJournalPrompt(contentMode: string = 'FULL', params?: { category?: string }): Promise<any> {
        const queryParams = new URLSearchParams({ content_mode: contentMode });
        if (params?.category) queryParams.append('category', params.category);
        
        const { data } = await apiClient.get(`/api/journal-prompts/prompts/random?${queryParams.toString()}`);
        return data;
    }

    async startGuidedJournalSession(sessionData: {
        contentMode?: string;
        sessionType?: string;
        duration?: number;
    }): Promise<any> {
        const { data } = await apiClient.post('/api/journal-prompts/guided-session', sessionData);
        return data;
    }

    // --- JOURNAL ENTRIES ---
    async getJournalEntries(userId: string, limit: number = 10): Promise<any> {
        const { data } = await apiClient.get(`/api/journal/${userId}/entries?limit=${limit}`);
        return data;
    }

    async createJournalEntry(entryData: {
        userId: string;
        text: string;
        contentMode?: string;
    }): Promise<any> {
        const { data } = await apiClient.post('/api/journal/entry', {
            userId: entryData.userId,  // Send as camelCase - backend will handle conversion
            text: entryData.text,
            contentMode: entryData.contentMode,  // Send as camelCase - backend will handle conversion
        });
        return data;
    }

    async getJournalSummaries(userId: string, limit: number = 5): Promise<any> {
        const { data } = await apiClient.get(`/api/journal/${userId}/summaries?limit=${limit}`);
        return data;
    }

    // --- JOURNAL PRIVACY SETTINGS ---
    async getJournalPrivacySettings(childId: string): Promise<any> {
        const { data } = await apiClient.get(`/api/journal/privacy-settings/${childId}`);
        return data;
    }

    async updateJournalPrivacySettings(settingsData: {
        userId: string;
        parentId: string;
        accessLevel: string;
        allowAiReflection: boolean;
        allowSymbolicTags: boolean;
    }): Promise<any> {
        const { data } = await apiClient.put('/api/journal/privacy-settings', settingsData);
        return data;
    }

    // --- TOKENS ---
    async getTokenBalance(userId?: string) {
        const activeUserId = userId || this.getActiveUserId();
        const { data } = await apiClient.get(`${this.endpoints.TOKENS}/${activeUserId}`);
        return data;
    }

    // REMOVED: getTokenSummary() - now handled by comprehensive getTokenBalance() endpoint

    async getTokenPackages() {
        const { data } = await apiClient.get(this.endpoints.TOKEN_PACKAGES);
        return data;
    }

    async createCheckoutSession(packageId: string, userId?: string) {
        const user = userId || this.getCurrentUserId();
        const { data } = await apiClient.post(`${this.endpoints.TOKEN_PACKAGES}/checkout`, {
            package_id: packageId,
            user_id: user
        });
        return data;
    }

    // Memory Pattern Feedback
    async submitMemoryFeedback(feedback: {
        memoryId: string;
        accuracy: 'helpful' | 'somewhat' | 'not_helpful';
        comment?: string;
        suggestedTrigger?: string;
        timestamp: number;
    }): Promise<{ success: boolean; feedbackId: string }> {
        const { data } = await apiClient.post(`${this.endpoints.SYMBOLIC}/memory-feedback`, feedback);
        return data;
    }

    // Admin Memory Pattern Management
    async getAllMemoryPatterns(): Promise<{ patterns: any[]; totalCount: number; timestamp: number }> {
        const { data } = await apiClient.get(`${this.endpoints.SYMBOLIC}/admin/memory-patterns`);
        return data;
    }

    async createMemoryPattern(patternData: {
        trigger: string;
        tags: string[];
        responseTemplate: string;
        intensity: number;
        contentModes: string[];
    }): Promise<{ success: boolean; patternId: string; message: string }> {
        const { data } = await apiClient.post(`${this.endpoints.SYMBOLIC}/admin/memory-patterns`, patternData);
        return data;
    }

    async updateMemoryPattern(patternId: string, patternData: any): Promise<{ success: boolean; message: string }> {
        const { data } = await apiClient.put(`${this.endpoints.SYMBOLIC}/admin/memory-patterns/${patternId}`, patternData);
        return data;
    }

    async deleteMemoryPattern(patternId: string): Promise<{ success: boolean; message: string }> {
        const { data } = await apiClient.delete(`${this.endpoints.SYMBOLIC}/admin/memory-patterns/${patternId}`);
        return data;
    }

    async getMemoryAnalytics(): Promise<{ analytics: any; timestamp: number }> {
        const { data } = await apiClient.get(`${this.endpoints.SYMBOLIC}/admin/memory-analytics`);
        return data;
    }

    async getFlaggedPatterns(): Promise<{ flaggedPatterns: any[]; totalCount: number; timestamp: number }> {
        const { data } = await apiClient.get(`${this.endpoints.SYMBOLIC}/admin/flagged-patterns`);
        return data;
    }

    async resolveFlaggedPattern(flagId: string, resolutionData: {
        action: 'approve' | 'reject' | 'modify';
        reason?: string;
        modifications?: any;
    }): Promise<{ success: boolean; message: string }> {
        const { data } = await apiClient.post(`${this.endpoints.SYMBOLIC}/admin/flagged-patterns/${flagId}/resolve`, resolutionData);
        return data;
    }

    // --- SYSTEM PROMPT ---
    async getSystemPrompt(model: string): Promise<SystemPromptData> {
        const contentMode = localStorage.getItem('contentMode');
        const params = contentMode ? `?content_mode=${contentMode}` : '';
        const { data } = await apiClient.get<SystemPromptData>(`${this.endpoints.SYSTEM_PROMPT}/${model}${params}`);
        return data;
    }

    // --- SYMBOLIC TAGS ---
    async getSymbolicTags(userId: string) {
        try {
            const { data } = await apiClient.get<{ tags: string[] }>(`/api/symbolic-tags/${userId}`);
            return data.tags;
        } catch (error: any) {
            // CRITICAL: Never return empty data in production mental health app
            if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK' || error.response?.status === 503) {
                throw new Error('Symbolic tags unavailable. Please ensure backend is running.');
            }
            throw error;
        }
    }

    // --- SYMBOLIC OVERRIDE ---
    async submitSymbolicOverride(overrideData: {
        userId: string;
        messageId: string;
        originalTags: string[];
        correctedTags: string[];
        timestamp: string;
    }): Promise<{ message: string }> {
        const { data } = await apiClient.post('/api/symbolic-override', overrideData);
        return data;
    }

    // --- RADAR METRICS ---
    async getRadarMetrics(): Promise<{
        axes: Record<string, number>;
        sourceTags: string[];
        isSynthetic: boolean;
    }> {
        try {
            const { data } = await apiClient.get('/api/metrics/radar-chart');
            return data;
        } catch (error: any) {
            // CRITICAL: Never return synthetic data in production mental health app
            // This could lead to dangerous misdiagnosis or treatment errors
            console.error('Radar metrics unavailable:', error);
            throw new Error('Radar metrics unavailable. Please ensure backend is running.');
        }
    }

    // --- UNIFIED SYMBOLIC SCORING ---
    async getUnifiedSymbolicScoring(params: {
        userId?: string;
        appType?: 'bibbit';
        contentMode?: 'G' | 'PG' | 'FULL';
        testMode?: boolean;
        days?: number;
    } = {}): Promise<{
        userId: string;
        appType: string;
        contentMode: string;
        safeForBibbit: boolean;
        radarScores: Record<string, number>;
        sourceTags: string[];
        tagFrequency: Record<string, number>;
        patternInsights: any[];
        isSynthetic: boolean;
        timestamp: string;
        analysisPeriod: {
            startDate: string;
            endDate: string;
            days: number;
        };
        debug?: any;
    }> {
        try {
            // Force appType to 'bibbit' for this clean service
            const bibbitParams = { ...params, appType: 'bibbit' as const };
            const { data } = await apiClient.post('/api/metrics/symbolic-scoring', bibbitParams);
            return data;
        } catch (error: any) {
            // CRITICAL: Never return synthetic data in production mental health app
            // This could lead to dangerous misdiagnosis or treatment errors
            console.error('Symbolic scoring unavailable:', error);
            throw new Error('Symbolic scoring unavailable. Please ensure backend is running.');
        }
    }

    // --- SYMBOLIC SYSTEM METHODS ---
    async extractEmotionalMetrics(data: {
        userMessage: string;
        aiResponse: string;
        contentMode: string;
        userId: string;
    }): Promise<{
        metrics: Record<string, number>;
        tags: string[];
        content_mode: string;
        analysis_timestamp: number;
        user_id: string;
    }> {
        try {
            // Backend expects camelCase fields
            const { data: responseData } = await apiClient.post(`${this.endpoints.SYMBOLIC}/extract-emotional-metrics`, data);
            return responseData;
        } catch (error: any) {
            // CRITICAL: Never return synthetic data in production mental health app
            console.error('Emotional metrics extraction unavailable:', error);
            throw new Error('Emotional metrics extraction unavailable. Please ensure backend is running.');
        }
    }

    async saveSymbolicData(_data: {
        userId: string;
        conversationId: string;
        tags: string[];
        contentMode: string;
        timestamp: number;
    }): Promise<{ success: boolean; message: string }> {
        try {
            // Symbolic data is automatically saved in the extract-emotional-metrics endpoint
            // No separate save endpoint needed
            return {
                success: true,
                message: 'Symbolic data automatically saved via extract-emotional-metrics'
            };
        } catch (error: any) {
            // CRITICAL: Never return synthetic data in production mental health app
            console.error('Symbolic data save unavailable:', error);
            throw new Error('Symbolic data save unavailable. Please ensure backend is running.');
        }
    }

    async extractSymbolicTags(data: {
        userMessage: string;
        aiResponse: string;
        contentMode: string;
        userId: string;
    }): Promise<{
        tags: string[];
        confidence: number;
        timestamp: number;
    }> {
        try {
            const { data: responseData } = await apiClient.post('/api/symbolic/extract-tags', data);
            return responseData;
        } catch (error: any) {
            // CRITICAL: Never return synthetic data in production mental health app
            console.error('Symbolic tag extraction unavailable:', error);
            throw new Error('Symbolic tag extraction unavailable. Please ensure backend is running.');
        }
    }

    async getMetrics(userId: string): Promise<{
        radarScores: Record<string, number>;
        sourceTags: string[];
        timestamp: number;
    }> {
        try {
            const { data } = await apiClient.get(`/api/symbolic/metrics/${userId}`);
            return data;
        } catch (error: any) {
            // CRITICAL: Never return synthetic data in production mental health app
            console.error('Metrics unavailable:', error);
            throw new Error('Metrics unavailable. Please ensure backend is running.');
        }
    }

    // --- INSIGHT FEEDBACK ---
    async submitInsightFeedback(feedbackData: {
        userId: string;
        insightId: string;
        feedback: 'accurate' | 'inaccurate';
        timestamp: string;
    }): Promise<{ message: string }> {
        const { data } = await apiClient.post('/api/insight-feedback', feedbackData);
        return data;
    }

    // --- THERAPIST FUNCTIONS ---
    async getTherapistPatients(therapistId: string): Promise<{
        patients: Array<{
            uid: string;
            displayName: string;
            email: string;
            status: string;
            diagnosis?: string;
            sessionsCompleted?: number;
            lastActive?: string;
            nextAppointment?: string;
        }>;
    }> {
        try {
            const { data } = await apiClient.get(`/api/therapist/${therapistId}/patients`);
            return data;
        } catch (error: any) {
            console.error('Error fetching therapist patients:', error);
            // Return empty patients array for testing
            return {
                patients: []
            };
        }
    }

    async getPatient(patientId: string): Promise<any> {
        try {
            // Get the current user's ID to use as therapist_id
            const currentUserId = this.getCurrentUserId();
            const { data } = await apiClient.get(`/api/therapist/${currentUserId}/patients`);
            
            // DEBUG: Log the API response
            console.log('🔧 [API SERVICE] getPatient response:', data);
            console.log('🔧 [API SERVICE] Looking for patient ID:', patientId);
            console.log('🔧 [API SERVICE] Available patients:', data.patients?.map((p: any) => ({ uid: p.uid, displayName: p.displayName })));
            
            // Find the specific patient in the list
            const patients = data.patients || [];
            const patient = patients.find((p: any) => p.uid === patientId);
            
            console.log('🔧 [API SERVICE] Found patient:', patient);
            
            if (!patient) {
                throw new Error(`Patient with ID ${patientId} not found`);
            }
            
            return patient;
        } catch (error: any) {
            console.error('Error fetching patient:', error);
            // CRITICAL: Never return mock data in production mental health app
            // This could lead to dangerous misdiagnosis or treatment errors
            throw new Error('Patient data unavailable. Please ensure backend is running and patient exists.');
        }
    }

    async getTherapistNotes(patientId: string): Promise<any[]> {
        try {
            const currentUserId = this.getCurrentUserId();
            const { data } = await apiClient.get(`/api/mental-health/therapist-notes/${patientId}`);
            return data;
        } catch (error: any) {
            console.error('Error fetching therapist notes:', error);
            // Return empty array - never mock clinical data
            return [];
        }
    }

    async getPatientAlerts(patientId: string): Promise<any[]> {
        try {
            const currentUserId = this.getCurrentUserId();
            const { data } = await apiClient.get(`/api/therapist/${currentUserId}/patients/${patientId}/alerts`);
            return data;
        } catch (error: any) {
            console.error('Error fetching patient alerts:', error);
            // Return empty array - never mock clinical data
            return [];
        }
    }

    async getPatientInsights(patientId: string): Promise<any[]> {
        try {
            // For now, return empty array since there's no specific insights endpoint
            // This could be implemented later when the backend has an insights endpoint
            return [];
        } catch (error: any) {
            console.error('Error fetching patient insights:', error);
            // Return empty array - never mock clinical data
            return [];
        }
    }

    // Generic POST method for flexibility
    async post<T>(endpoint: string, data: any): Promise<T> {
        const response = await apiClient.post<T>(endpoint, data);
        return response.data;
    }
}

// Export a single instance for the entire application
const apiService = new BibbitApiService();
export default apiService;

// Also export apiClient for direct use with react-query
export { apiClient };